import { SafeHtml } from '@angular/platform-browser';
import {
  HorizontalListData,
  HorizontalListItemType,
  HorizontalListItemSize,
  HorizontalFactoryKeyValueBaseItem,
  HorizontalFactoryBaseItem,
  HorizontalListOptionType,
  HorizontalListOptionData,
} from './horizontal-list.model';

/**
 * Factory for creating ready-to-use HorizontalListData objects.
 */
export class HorizontalListFactory {
  /**
   * Creates a simple String item (title + displayedValue).
   */
  static createKeyValueStringItem(options: {
    displayedValue?: string
    isValueHidden?: boolean
    valueTooltipMessage?: string
  } & HorizontalFactoryKeyValueBaseItem): HorizontalListData {
    return {
      ...HorizontalListFactory.createBaseKeyValueItem(options),
      valueData: {
        optionType: HorizontalListOptionType.String,
        displayedValue: options.displayedValue,
        isOptionHidden: options.isValueHidden,
        tooltipMessage: options.valueTooltipMessage
      },
    };
  }

  /**
   * Creates a title + value item with an internal router link.
   */
  static createKeyValueRouterLinkItem(options: {
    routerLink?: string;
    actionName?: string;
    displayedValue?: string;
    isValueHidden?: boolean
    valueTooltipMessage?: string
  } & HorizontalFactoryKeyValueBaseItem): HorizontalListData {
    let optionType = HorizontalListOptionType.RouterLink;
    if (!options.routerLink && !options.actionName && !!options.displayedValue) {
      optionType = HorizontalListOptionType.String;
    }

    return {
      ...HorizontalListFactory.createBaseKeyValueItem(options),
      valueData: {
        optionType,
        displayedValue: options.displayedValue,
        routerLink: options.routerLink,
        actionName: options.actionName,
        isOptionHidden: options.isValueHidden,
        tooltipMessage: options.valueTooltipMessage
      },
    };
  }

  /**
   * Creates a title + value item with an external link.
   */
  static createKeyValueExternalLinkItem(options: {
    externalLink?: string;
    actionName?: string;
    displayedValue?: string;
    isValueHidden?: boolean
    valueTooltipMessage?: string
  } & HorizontalFactoryKeyValueBaseItem): HorizontalListData {
    let optionType = HorizontalListOptionType.ExternalLink;
    if (!options.externalLink && !options.actionName && !!options.displayedValue) {
      optionType = HorizontalListOptionType.String;
    }

    return {
      ...HorizontalListFactory.createBaseKeyValueItem(options),
      valueData: {
        optionType,
        displayedValue: options.displayedValue,
        externalLink: options.externalLink,
        actionName: options.actionName,
        isOptionHidden: options.isValueHidden,
        tooltipMessage: options.valueTooltipMessage
      },
    };
  }

  /**
   * Creates a title + value item with a custom VALUE template.
   * Title remains static; only the right‐side value is rendered via the given template.
   * @param customTemplateName the name of the ng-template for the value
   */
  static createKeyValueCustomValueTemplateItem(options: {
    customTemplateName: string;
  } & HorizontalFactoryKeyValueBaseItem): HorizontalListData {
    return {
      ...HorizontalListFactory.createBaseKeyValueItem(options),
      valueData: {
        optionType: HorizontalListOptionType.CustomTemplate,
        customTemplateName: options.customTemplateName
      },
    };
  }

  /**
  * Creates a custom title + value item.
  */
  static createKeyValueCustomItem(options: {
    titleData?: HorizontalListOptionData,
    valueData?: HorizontalListOptionData
  } & HorizontalFactoryBaseItem): HorizontalListData {
    return {
      ...HorizontalListFactory.createBaseItem(options),
      itemType: HorizontalListItemType.TitleValue,
      titleData: options.titleData,
      valueData: options.valueData
    };
  }

  /**
   * Creates an Info‐type (value only) item that renders a String value.
   */
  static createInfoStringItem(options: {
    displayedValue: string | SafeHtml;
  } & HorizontalFactoryBaseItem): HorizontalListData {
    return {
      ...HorizontalListFactory.createBaseItem(options),
      itemType: HorizontalListItemType.Info,
      valueData: {
        optionType: HorizontalListOptionType.String,
        displayedValue: options.displayedValue
      },
    };
  }

  /**
   * Creates an Info‐type (value only) item that renders a RouterLink.
   */
  static createInfoRouterLinkItem(options: {
    routerLink?: string;
    actionName?: string;
    displayedValue?: string;
  } & HorizontalFactoryBaseItem): HorizontalListData {
    let optionType = HorizontalListOptionType.RouterLink;
    if (!options.routerLink && !options.actionName && !!options.displayedValue) {
      optionType = HorizontalListOptionType.String;
    }

    return {
      ...HorizontalListFactory.createBaseItem(options),
      itemType: HorizontalListItemType.Info,
      valueData: {
        optionType,
        routerLink: options.routerLink,
        displayedValue: options.displayedValue,
        actionName: options.actionName
      },
    };
  }

  /**
   * Creates an Info‐type (value only) item that renders an ExternalLink.
   */
  static createInfoExternalLinkItem(options: {
    externalLink?: string;
    actionName?: string;
    displayedValue?: string;
  } & HorizontalFactoryBaseItem): HorizontalListData {
    let optionType = HorizontalListOptionType.ExternalLink;
    if (!options.externalLink && !options.actionName && !!options.displayedValue) {
      optionType = HorizontalListOptionType.String;
    }

    return {
      ...HorizontalListFactory.createBaseItem(options),
      itemType: HorizontalListItemType.Info,
      valueData: {
        optionType,
        displayedValue: options.displayedValue,
        externalLink: options.externalLink,
        actionName: options.actionName
      },
    };
  }

  /**
 * Creates a custom item with a custom template.
 * @param customTemplateName the name of the ng-template
 */
  static createCustomTemplateItem(options: {
    customTemplateName: string;
  } & HorizontalFactoryBaseItem): HorizontalListData {
    return {
      ...HorizontalListFactory.createBaseItem(options),
      itemType: HorizontalListItemType.Custom,
      customItemTemplateName: options.customTemplateName,
    };
  }

  private static createBaseKeyValueItem(options: HorizontalFactoryKeyValueBaseItem): HorizontalListData {
    return {
      ...HorizontalListFactory.createBaseItem(options),
      itemType: HorizontalListItemType.TitleValue,
      titleData: {
        optionType: HorizontalListOptionType.String,
        displayedValue: options.title
      }
    };
  }

  private static createBaseItem(options: HorizontalFactoryBaseItem): HorizontalListData {
    return {
      isItemHidden: options.isItemHidden ?? false,
      itemSize: options.itemSize ?? HorizontalListItemSize.Default,
      hideDividerBelow: options.hideDividerBelow ?? false
    };
  }
}
